import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity, AlertTriangle, Brain, GraduationCap, Loader2, Sparkles,
  TrendingUp, TrendingDown, Users, Wallet, Target, Lightbulb, ChevronRight, X,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { predictiveAnalytics } from "@/lib/predictive-analytics.functions";
import { cn } from "@/lib/utils";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/admin/predictive")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "Predictive Analytics · MAKAI" }] }),
  component: Page,
});

const UGX = (n: number) => new Intl.NumberFormat("en-UG").format(Math.round(n || 0));
const RISK_COLORS = { Critical: "#DC2626", High: "#F59E0B", Moderate: "#3B82F6", Low: "#16A34A" };

function Page() {
  const { active, loading } = useSchoolContext();
  const schoolId = active?.school_id ?? null;
  return (
    <AdminShell
      title="Predictive Analytics"
      subtitle="AI predictions and drill-down insights — every chart explains the why in plain English."
    >
      {loading ? <Loading /> : !schoolId ? <NoSchool /> : <Dashboard key={schoolId} schoolId={schoolId} />}

      <div className="mt-6">
        <AICallout
          description="Ask me to explain any chart on this page in plain English."
          suggestions={[
            "Explain the dropout-risk chart",
            "Which trend should I act on first?",
            "Predict next term's attendance",
            "Generate an executive summary",
          ]}
        />
      </div>
    </AdminShell>
  );
}
function Loading() { return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Loading predictions…</div>; }
function NoSchool() { return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Select a school to view predictive analytics.</div>; }

type Learner = Awaited<ReturnType<typeof predictiveAnalytics>>["learners"][number];

function Dashboard({ schoolId }: { schoolId: string }) {
  const run = useServerFn(predictiveAnalytics);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["predictive", schoolId],
    queryFn: () => run({ data: { schoolId } }),
  });
  const [selected, setSelected] = useState<Learner | null>(null);
  const [bandFilter, setBandFilter] = useState<"All" | "Critical" | "High" | "Moderate" | "Low">("All");

  const filtered = useMemo(
    () => (data?.learners ?? []).filter((l) => bandFilter === "All" || l.band === bandFilter),
    [data, bandFilter]
  );

  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm">
        <div className="font-semibold text-destructive">Could not load predictions</div>
        <div className="mt-1 text-muted-foreground">{(error as Error)?.message ?? "Unknown error"}</div>
        <Button className="mt-3" onClick={() => refetch()} size="sm">Retry</Button>
      </div>
    );

  const riskTotal = data.riskBuckets.Critical + data.riskBuckets.High + data.riskBuckets.Moderate + data.riskBuckets.Low;
  const riskPieData = (["Critical", "High", "Moderate", "Low"] as const).map((k) => ({ name: k, value: data.riskBuckets[k] }));
  const attTrend = data.attendance.combined;
  const splitWeekIdx = data.attendance.history.length; // forecast starts after this index

  return (
    <div className="space-y-6">
      {/* Headline tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile icon={<Target className="h-4 w-4" />} label="School Performance Index" value={`${data.performanceIndex}`}
          tone={data.performanceIndex >= 70 ? "success" : data.performanceIndex >= 50 ? "info" : "warn"}
          caption="0–100 composite score" />
        <Tile icon={<AlertTriangle className="h-4 w-4" />} label="Critical-risk learners" value={`${data.riskBuckets.Critical}`}
          tone={data.riskBuckets.Critical > 0 ? "danger" : "success"} caption={`of ${data.totalLearners} active`} />
        <Tile icon={<Activity className="h-4 w-4" />} label="Predicted attendance (4w)"
          value={`${data.attendance.forecast.at(-1)?.attendancePct ?? "—"}%`} tone="info"
          caption="linear projection" />
        <Tile icon={<Wallet className="h-4 w-4" />} label="Fee clearance (term)"
          value={`${data.fee.clearanceProbabilityPct}%`}
          tone={data.fee.clearanceProbabilityPct >= 70 ? "success" : data.fee.clearanceProbabilityPct >= 40 ? "warn" : "danger"}
          caption="projected" />
      </div>

      {/* School Performance Index */}
      <Card title="School Performance Index" icon={<Target className="h-4 w-4 text-primary" />}
        explain="A composite of academics, attendance, fees collection and learner engagement. Each component is shown below with its weight so you can see what's lifting or pulling the index.">
        <div className="grid gap-4 sm:grid-cols-[140px_1fr] sm:items-center">
          <div className="flex flex-col items-center justify-center rounded-2xl bg-primary-soft p-4">
            <div className="font-display text-4xl font-bold text-primary">{data.performanceIndex}</div>
            <div className="mt-1 text-xs uppercase tracking-wide text-primary/80">/ 100</div>
          </div>
          <div className="space-y-2">
            {data.performanceIndexComponents.map((c) => (
              <Bar2 key={c.label} label={c.label} value={c.value} weightPct={c.weightPct} />
            ))}
          </div>
        </div>
      </Card>

      {/* Risk distribution + drill-down */}
      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <Card title="Student Risk Score distribution" icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
          explain={`Of ${riskTotal} active learners, ${data.riskBuckets.Critical} are Critical (score ≥70) and ${data.riskBuckets.High} are High (55–69). Click a band on the right to drill in.`}>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={riskPieData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                  {riskPieData.map((d, i) => <Cell key={i} fill={RISK_COLORS[d.name as keyof typeof RISK_COLORS]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="At-risk learners — drill down"
          icon={<Users className="h-4 w-4 text-primary" />}
          explain="Click any row to see the learner's subject-by-subject performance, attendance and the exact reasons flagged."
          action={
            <div className="flex gap-1 overflow-x-auto">
              {(["All", "Critical", "High", "Moderate", "Low"] as const).map((b) => (
                <button key={b} onClick={() => setBandFilter(b)}
                  className={cn("rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
                    bandFilter === b ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70")}>
                  {b}
                </button>
              ))}
            </div>
          }>
          <div className="max-h-80 overflow-y-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/70 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="py-2 px-3">Learner</th><th className="py-2 px-3">Risk</th><th className="py-2 px-3">Dropout</th><th className="py-2 px-3">Avg</th><th className="py-2 px-3">Att.</th><th className="py-2 px-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.slice(0, 30).map((l) => (
                  <tr key={l.learnerId} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelected(l)}>
                    <td className="py-2 px-3">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-[11px] text-muted-foreground">{l.admissionNo ?? "—"}</div>
                    </td>
                    <td className="py-2 px-3"><RiskPill band={l.band} score={l.risk} /></td>
                    <td className="py-2 px-3 font-mono text-xs">{l.dropoutPct}%</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.avgScorePct ?? "—"}{l.avgScorePct != null && "%"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.attendancePct ?? "—"}{l.attendancePct != null && "%"}</td>
                    <td className="py-2 px-3 text-muted-foreground"><ChevronRight className="h-4 w-4" /></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No learners in this band — good news.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Attendance prediction */}
      <Card title="Attendance Prediction (next 4 weeks)" icon={<Activity className="h-4 w-4 text-primary" />}
        explain={`We fitted a straight-line trend through the past ${data.attendance.history.length} weeks. The shaded area on the right is the forecast. If the trend is falling, expect lower attendance unless interventions kick in.`}>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={attTrend}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="week" fontSize={11} />
              <YAxis fontSize={11} domain={[0, 100]} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <ReferenceLine x={attTrend[splitWeekIdx - 1]?.week} stroke="#94a3b8" strokeDasharray="3 3" label={{ value: "Today", fontSize: 10, fill: "#64748b" }} />
              <Line type="monotone" dataKey="attendancePct" stroke="#0F766E" strokeWidth={2}
                dot={(props: { cx?: number; cy?: number; index?: number }) => {
                  const isForecast = (props.index ?? 0) >= splitWeekIdx;
                  return <circle cx={props.cx} cy={props.cy} r={3} fill={isForecast ? "#D97706" : "#0F766E"} />;
                }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Green dots = actual; amber dots = forecast. Forecast value at week +4: <b>{data.attendance.forecast.at(-1)?.attendancePct ?? "—"}%</b>.
        </div>
      </Card>

      {/* Exam prediction */}
      <Card title="Exam Prediction by Subject" icon={<GraduationCap className="h-4 w-4 text-primary" />}
        explain={`School-wide we project ${data.examPrediction.schoolPredictedPct}% — a blend of recent assessment averages (70%) and 30-day attendance (30%). The chart compares each subject's current average to its projected exam score.`}>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.examPrediction.bySubject}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="subject" fontSize={10} angle={-15} textAnchor="end" height={50} />
              <YAxis fontSize={11} domain={[0, 100]} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="currentPct" name="Current avg" fill="#94a3b8" radius={[6, 6, 0, 0]} />
              <Bar dataKey="predictedPct" name="Predicted exam" fill="#0F766E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Dropout prediction */}
      <Card title="Dropout Prediction" icon={<TrendingDown className="h-4 w-4 text-destructive" />}
        explain="Dropout probability blends recent absences, very-low scores and missing assessment records. Learners with ≥50% probability need a guardian conversation within 10 days.">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Mini label="Very high (≥50%)" value={String(data.dropoutBuckets.veryHigh)} tone="danger" />
          <Mini label="High (30–49%)" value={String(data.dropoutBuckets.high)} tone="warn" />
          <Mini label="Moderate (15–29%)" value={String(data.dropoutBuckets.moderate)} tone="info" />
          <Mini label="Low (<15%)" value={String(data.dropoutBuckets.low)} tone="success" />
        </div>
        {data.topDropout.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/70 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="py-2 px-3">Learner</th><th className="py-2 px-3">Dropout %</th><th className="py-2 px-3">Attendance</th><th className="py-2 px-3">Recent absences</th><th className="py-2 px-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {data.topDropout.slice(0, 10).map((l) => (
                  <tr key={l.learnerId} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelected(l)}>
                    <td className="py-2 px-3"><div className="font-medium">{l.name}</div><div className="text-[11px] text-muted-foreground">{l.admissionNo ?? "—"}</div></td>
                    <td className="py-2 px-3 font-mono text-xs">{l.dropoutPct}%</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.attendancePct ?? "—"}{l.attendancePct != null && "%"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.recentMissed}</td>
                    <td className="py-2 px-3 text-muted-foreground"><ChevronRight className="h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Fee payment prediction */}
      <Card title="Fee Payment Prediction" icon={<Wallet className="h-4 w-4 text-primary" />}
        explain={data.fee.explanation}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Mini label="Collected 90d" value={`UGX ${UGX(data.fee.trailingCollected90dUgx)}`} />
          <Mini label="Predicted 30d" value={`UGX ${UGX(data.fee.predictedNext30dUgx)}`} tone="info" />
          <Mini label="Outstanding" value={`UGX ${UGX(data.fee.outstandingUgx)}`} tone="warn" />
          <Mini label="Overdue invoices" value={String(data.fee.overdueInvoices)} tone={data.fee.overdueInvoices > 0 ? "danger" : "success"} />
        </div>
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-xs"><span>Probability outstanding cleared this term</span><span className="font-mono">{data.fee.clearanceProbabilityPct}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full" style={{ width: `${data.fee.clearanceProbabilityPct}%`, background: data.fee.clearanceProbabilityPct >= 70 ? "#16A34A" : data.fee.clearanceProbabilityPct >= 40 ? "#F59E0B" : "#DC2626" }} />
          </div>
        </div>
      </Card>

      {/* Teacher workload */}
      <Card title="Teacher Workload" icon={<GraduationCap className="h-4 w-4 text-primary" />}
        explain={`Workload index = 5×classes + 4×subjects + 3×assessments(30d) + 2×lesson plans(30d), capped at 100. ${data.workload.counts.overloaded} teachers are overloaded; ${data.workload.counts.inactive} appear inactive (no recent assignments or plans).`}>
        <div className="mb-3 grid grid-cols-4 gap-2">
          <Mini label="Overloaded" value={String(data.workload.counts.overloaded)} tone="danger" />
          <Mini label="Balanced" value={String(data.workload.counts.balanced)} tone="success" />
          <Mini label="Light" value={String(data.workload.counts.light)} tone="info" />
          <Mini label="Inactive" value={String(data.workload.counts.inactive)} tone="warn" />
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.workload.teachers.slice(0, 12)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis type="number" fontSize={11} domain={[0, 100]} />
              <YAxis type="category" dataKey="name" fontSize={10} width={120} />
              <Tooltip />
              <Bar dataKey="workloadIndex" radius={[0, 6, 6, 0]}>
                {data.workload.teachers.slice(0, 12).map((t, i) => (
                  <Cell key={i} fill={t.workloadIndex >= 75 ? "#DC2626" : t.workloadIndex >= 40 ? "#16A34A" : t.workloadIndex >= 15 ? "#3B82F6" : "#94A3B8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Suggested Interventions */}
      <Card title="Suggested Interventions" icon={<Lightbulb className="h-4 w-4 text-primary" />}
        explain="Generated from the live signals above. Items are ordered by priority — start at the top.">
        <ul className="space-y-2">
          {data.interventions.map((iv, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl border bg-card p-3">
              <span className={cn("mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                iv.priority === "high" ? "bg-red-100 text-red-700" : iv.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700")}>
                {iv.priority === "high" ? "!" : iv.priority === "medium" ? "•" : "✓"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold">{iv.title}</div>
                  {iv.metric && <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-mono">{iv.metric}</span>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{iv.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {/* Methodology */}
      <Card title="How these predictions work" icon={<Brain className="h-4 w-4 text-primary" />}
        explain="MAKAI uses transparent, rule-based models so principals can trust and challenge every number. We do not hide behind a black box.">
        <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
          <li><b>Risk Score</b> = 0.50 × (100 − avg score) + 0.35 × (100 − attendance) + 0.15 × engagement penalty.</li>
          <li><b>Dropout %</b> rises with low attendance (&lt;60%), low scores (&lt;45%), recent absences (≥3 in 14d) and missing marks.</li>
          <li><b>Exam Prediction</b> = 0.70 × recent assessment average + 0.30 × 30-day attendance.</li>
          <li><b>Attendance Forecast</b> uses least-squares linear regression on the last 8 weeks.</li>
          <li><b>Fee Prediction</b> projects the trailing-90-day daily collection forward 30 days.</li>
          <li><b>Performance Index</b> = 0.35×academics + 0.25×attendance + 0.20×fees + 0.20×engagement.</li>
        </ul>
      </Card>

      <LearnerDialog learner={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

// -------- Components --------
function LearnerDialog({ learner, onClose }: { learner: Learner | null; onClose: () => void }) {
  return (
    <Dialog open={!!learner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {learner && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{learner.name}<RiskPill band={learner.band} score={learner.risk} /></DialogTitle>
              <DialogDescription>{learner.admissionNo ?? "No admission number"} · {learner.assessmentsTaken} assessments · {learner.recentMissed} absences in last 14 days</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2">
              <Mini label="Risk score" value={`${learner.risk}/100`} tone={learner.band === "Critical" ? "danger" : learner.band === "High" ? "warn" : "info"} />
              <Mini label="Dropout %" value={`${learner.dropoutPct}%`} tone={learner.dropoutPct >= 50 ? "danger" : learner.dropoutPct >= 30 ? "warn" : "success"} />
              <Mini label="Predicted exam" value={`${learner.predictedExamPct}%`} tone={learner.predictedExamPct >= 60 ? "success" : "warn"} />
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Why flagged</div>
              {learner.reasons.length === 0 ? (
                <p className="text-sm text-muted-foreground">No specific risk factors — appears healthy.</p>
              ) : (
                <ul className="ml-4 list-disc space-y-1 text-sm">{learner.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              )}
            </div>
            {learner.subjects.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Subject performance</div>
                <div className="space-y-1.5">
                  {learner.subjects.map((s) => (
                    <div key={s.subject}>
                      <div className="flex items-center justify-between text-xs"><span>{s.subject}</span><span className="font-mono">{s.avgPct}% · {s.samples}</span></div>
                      <div className="mt-0.5 h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full" style={{ width: `${s.avgPct}%`, background: s.avgPct >= 60 ? "#16A34A" : s.avgPct >= 45 ? "#F59E0B" : "#DC2626" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-lg bg-primary-soft p-3 text-sm">
              <div className="mb-1 flex items-center gap-2 font-semibold text-primary"><Sparkles className="h-4 w-4" />Suggested next step</div>
              {learner.dropoutPct >= 50
                ? "Schedule a guardian meeting within 10 days. Document a written support plan covering attendance and academic catch-up."
                : learner.band === "Critical" || learner.band === "High"
                ? "Pair with a class teacher mentor, run a remedial session in the weakest subject, and call the parent this week."
                : learner.band === "Moderate"
                ? "Monitor weekly. Consider a targeted practice quiz in the lowest-scoring subject."
                : "Keep current routine — share a positive note with the parent."}
            </div>
            <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={onClose}><X className="mr-1 h-4 w-4" />Close</Button></div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Card({ title, icon, explain, action, children }: { title: string; icon?: React.ReactNode; explain?: string; action?: React.ReactNode; children: React.ReactNode }) {
  const askMakai = () => {
    const prompt = `Explain the "${title}" chart in plain English. ${explain ?? ""} What should I do about it?`.trim();
    window.dispatchEvent(new CustomEvent("makai:ask", { detail: { prompt } }));
  };
  return (
    <section className="rounded-2xl border border-border/70 bg-card p-4 shadow-card sm:p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</div>
        <div className="flex items-center gap-1">
          {action}
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={askMakai}>
            <Sparkles className="mr-1 h-3.5 w-3.5 text-primary" />Explain
          </Button>
        </div>
      </div>
      {explain && <p className="mb-3 text-xs text-muted-foreground">{explain}</p>}
      {children}
    </section>
  );
}
function Tile({ icon, label, value, caption, tone }: { icon: React.ReactNode; label: string; value: string; caption?: string; tone: "primary" | "success" | "info" | "warn" | "danger" }) {
  const map: Record<string, string> = {
    primary: "bg-primary-soft text-primary",
    success: "bg-emerald-100 text-emerald-700",
    info: "bg-sky-100 text-sky-700",
    warn: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
  };
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-3 shadow-card">
      <div className={cn("mb-2 inline-flex h-7 w-7 items-center justify-center rounded-lg", map[tone])}>{icon}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-display text-lg font-bold">{value}</div>
      {caption && <div className="mt-0.5 text-[11px] text-muted-foreground">{caption}</div>}
    </div>
  );
}
function Mini({ label, value, tone = "muted" }: { label: string; value: string; tone?: "muted" | "info" | "warn" | "success" | "danger" }) {
  const cls: Record<string, string> = {
    muted: "bg-muted/50 text-foreground",
    info: "bg-sky-50 text-sky-800",
    warn: "bg-amber-50 text-amber-800",
    success: "bg-emerald-50 text-emerald-800",
    danger: "bg-red-50 text-red-800",
  };
  return (
    <div className={cn("rounded-xl px-3 py-2 text-center", cls[tone])}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-display text-sm font-bold">{value}</div>
    </div>
  );
}
function Bar2({ label, value, weightPct }: { label: string; value: number; weightPct: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span>{label} <span className="text-muted-foreground">({weightPct}%)</span></span>
        <span className="font-mono">{value}%</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  );
}
function RiskPill({ band, score }: { band: string; score: number }) {
  const tone: Record<string, string> = {
    Critical: "bg-red-100 text-red-700",
    High: "bg-amber-100 text-amber-700",
    Moderate: "bg-sky-100 text-sky-700",
    Low: "bg-emerald-100 text-emerald-700",
  };
  return <span className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold", tone[band])}>{band}<span className="font-mono opacity-70">{score}</span></span>;
}
